import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import cs from 'classnames';
import _ from 'lodash';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { TextFilter, DropdownFilter, DateFilter } from './FilterFields';
import styles from './CustomTableFilters.module.scss';

const CustomTableFilters = ({
  filters,
  setFilters,
  columns,
  itemsPerRow,
}) => {
  const { t } = useTranslation();

  const [locFilters, setLocFilters] = useState(filters);

  const handleApply = () => {
    if (_.some(_.values(locFilters)) && !_.isEqual(locFilters, filters)) {
      setFilters(locFilters);
    }
  };

  const handleReset = () => {
    setLocFilters({});
    if (_.some(_.values(filters))) {
      setFilters({});
    }
  };

  const handleChange = ({ name, value }) => {
    const updatedFilters = _.pickBy(
      {
        ...locFilters,
        [name]: value,
      },
      _.identity,
    );
    setLocFilters(updatedFilters);
  };

  const handleDateChange = ({ name, date, searchParamKey }) => {
    const updatedFilters = date instanceof Date
      ? {
        ...locFilters,
        [name]: { searchParamKey, value: format(date, 'dd-MM-yyyy') },
      }
      : _.keys(locFilters)
        .filter((key) => key !== name)
        .reduce((obj, curKey) => {
          obj[curKey] = locFilters[curKey]; // eslint-disable-line no-param-reassign
          return obj;
        }, {});

    setLocFilters(updatedFilters);
  };

  const filterColumns = _(columns)
    .filter((c) => c.filter)
    .reduce((result, col) => {
      const { filter } = col;

      if (filter.type === 'date') {
        filter.queryParams.map((item) => {
          const { operator, searchParamKey } = item;
          return result.push({ ...col, name: operator, searchParamKey });
        });
      } else {
        result.push(col);
      }

      return result;
    }, []);

  const blankInputs = _.fill(Array(itemsPerRow), {
    filter: { type: 'invisible' },
  });

  const rows = _.chunk(filterColumns, itemsPerRow).map((row) => blankInputs.map((blankInput, i) => {
    const field = row[i] || blankInput;
    return field;
  }));

  const getFilterField = (field) => {
    switch (field.filter.type) {
      case 'string':
        return TextFilter({ field, locFilters, handleChange });
      case 'dropdown':
        return DropdownFilter({ field, locFilters, handleChange });
      case 'date':
        return DateFilter({ field, locFilters, handleDateChange });
      default:
        return <input className={styles.invisibleInput} />;
    }
  };

  return (
    <div className={styles.filter_block}>
      {rows.map((row) => (
        <div
          key={`filterRow_${row[0].property}`}
          className={cs(styles.filter_inputs)}
        >
          {row.map((field) => (
            <div
              key={`field_${field.property}_${field.name}`}
              className={cs(styles.filter_col, {
                [styles.invisibleCol]: field.filter.type === 'invisible',
              })}
            >
              {getFilterField(field)}
            </div>
          ))}
        </div>
      ))}

      <div className={styles.filter_buttons}>
        <button
          type="button"
          className={cs(styles.reset_btn, 'white_button')}
          onClick={() => handleReset()}
        >
          {t('customTbl.filters.reset')}
        </button>
        <button
          type="button"
          className={cs(styles.apply_btn, 'blue_button')}
          onClick={() => handleApply()}
        >
          {t('customTbl.filters.apply')}
        </button>
      </div>
    </div>
  );
};

CustomTableFilters.propTypes = {
  filters: PropTypes.shape({}).isRequired,
  setFilters: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  itemsPerRow: PropTypes.number.isRequired,
};

export default CustomTableFilters;
